import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { SubscriptionDto } from "../../../services/dto";
import { SubscriptionService } from "./subscriptionService";
import { PlanService } from "../plan/planService";
import { SubscriptionPage } from "./subscriptionPage";
import { SubscriptionForm } from "./subscriptionForm";
import { SubscriptionView } from "./subscriptionView";
import { LoadingIndicator } from "../../../assets/vfx";
import { useSnackbar } from "../../../assets/vfx/snackbar/vfxSnackbarContext";
import { MessageSeverity } from "../../../assets/utils/lib/messageSeverity";
import { GRID_ACTION_TYPES, GridActionType } from "../../../assets/types";
import { Constants } from "../../../assets/utils/lib/constants";
import { apiErrorMessage } from "../../../assets/utils/lib/error";
import { isGridActionType } from "../../../assets/utils/helpers";
import { useActionFromQuery } from "../../../hooks/useActionFromQuery";

export const SubscriptionContainer = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const [data, setData] = useState<SubscriptionDto>();
  const [drawerType, setDrawerType] = useState<GridActionType>(
    GRID_ACTION_TYPES.HIDDEN
  );

  const subscriptionQuery = SubscriptionService.getSubscriptionQuery(data?.id);
  const plansQuery = PlanService.getPlansQuery();

  const { mutateAsync: saveSubscription } = SubscriptionService.save();
  const { mutate: deactivateSubscription } =
    SubscriptionService.deActivateSubscription();

  const isEditMode: boolean = !!(data?.id && data.id !== Constants.emptyGuid);
  const closeDrawer = (): void =>{
      setDrawerType(GRID_ACTION_TYPES.HIDDEN);
      clearSearchAction();
  }

  const refreshGrid = (): void => {
    isEditMode && subscriptionQuery.refetch();
    queryClient.invalidateQueries({ queryKey: ["subscriptionGridQuery"] });
    closeDrawer();
  };

  const handleApiResponse = (response: any, successMessage: string): void => {
    const errorMsg: string | null = apiErrorMessage(response.errorMessages);
    if (errorMsg) {
      showSnackbar(errorMsg, MessageSeverity.Error);
    } else {
      showSnackbar(successMessage, MessageSeverity.Success);
      refreshGrid();
    }
  };

  const handleSubmit = async (values: SubscriptionDto) => {
    values.id = isEditMode ? data!.id : Constants.emptyGuid;
    const message: string = isEditMode
      ? "Subscription updated"
      : "Subscription saved";

    return await saveSubscription(values, {
      onSuccess: (response) => handleApiResponse(response, message),
    });
  };

  const handleDeactivate = (): void => {
    const id: string = data?.id ?? Constants.emptyGuid;

    deactivateSubscription(id, {
      onSuccess: (response) =>
        handleApiResponse(response, "Subscription deactivated"),
    });
  };

  const handleEdit = (): void => {
    setData(subscriptionQuery.data);
    setDrawerType(GRID_ACTION_TYPES.EDIT);
  };

  const handleCustomEvent = (actionType: string, dataId: string): void => {
    if (isGridActionType(actionType)) {
      setDrawerType(actionType);
      setData({ id: dataId } as SubscriptionDto);
      setSearchAction(actionType, dataId);
    }
  };
  
  const { setSearchAction, clearSearchAction } = useActionFromQuery(handleCustomEvent);

  const renderDrawerContent = (): React.JSX.Element | null => {
    switch (drawerType) {
      case GRID_ACTION_TYPES.NEW:
        return (
          <SubscriptionForm show onHide={closeDrawer} onSave={handleSubmit} />
        );

      case GRID_ACTION_TYPES.EDIT:
        return (
          <SubscriptionForm
            show={!!subscriptionQuery.data}
            loading={subscriptionQuery.isLoading}
            data={subscriptionQuery.data}
            onHide={closeDrawer}
            onSave={handleSubmit}
          />
        );

      case GRID_ACTION_TYPES.VIEW:
      case GRID_ACTION_TYPES.DOUBLE_CLICK:
        return (
          <SubscriptionView
            show={!!subscriptionQuery.data}
            data={subscriptionQuery.data}
            onHide={closeDrawer}
            onDeactivate={handleDeactivate}
            onEdit={handleEdit}
          />
        );

      default:
        return null;
    }
  };

  if (plansQuery.isLoading) {
    return <LoadingIndicator isFullPage />;
  }

  return (
    <>
      <SubscriptionPage
        plans={plansQuery.data ?? []}
        onCustomEvent={handleCustomEvent}
      />
      {renderDrawerContent()}
    </>
  );
};
